/**
 * PromoRow Component
 * Displays promo cards using CardPromoInfo to match cart design
 * Supports images on left, no "Hemat" for free goods
 */

import { CardPromoInfo } from '@diamond/shared/ui';
import type { ItemInfo } from '@diamond/sol-buyer/utils';

interface PromoRowBaseProps {
  type: 'discount' | 'free-item' | 'cashback';
  duration: string;
  text: string;
  promoId: string;
  images?: string[]; // Optional images array
}

interface PromoRowFreeItem extends PromoRowBaseProps {
  type: 'free-item';
  item: ItemInfo;
}

interface PromoRowDiscount extends PromoRowBaseProps {
  type: 'discount';
  discountDisplay?: string; // Optional discount percentage text
}

interface PromoRowCashback extends PromoRowBaseProps {
  type: 'cashback';
}

type PromoRowProps = PromoRowFreeItem | PromoRowDiscount | PromoRowCashback;

/**
 * PromoRow - displays promos using CardPromoInfo
 * Matches cart free goods design with images on left
 */
export function PromoRow(props: PromoRowProps) {
  // Base props for all CardPromoInfo types
  const baseProps = {
    type: props.type,
    duration: props.duration,
    text: props.text,
    images: props.images,
    variant: 'checkout' as const,
  };

  // Free-item type - show item details, hide savings
  if (props.type === 'free-item') {
    return (
      <CardPromoInfo
        {...baseProps}
        type="free-item"
        item={props.item}
        showFreeItemPrice={false} // Don't show "Hemat Rp..." for free goods
        savings={props.item ? props.item.price * props.item.qty : undefined}
      />
    );
  }

  // Discount type - show discount percentage
  if (props.type === 'discount') {
    return (
      <CardPromoInfo
        {...baseProps}
        type="discount"
        discountDisplay={props.discountDisplay}
      />
    );
  }

  // Cashback type - default display
  return <CardPromoInfo {...baseProps} type="cashback" />;
}
